// Budget charts. The funding chart shows one edition's years as bars (actual, current, request, then
// plans) with the previous edition's plan as markers, on one axis in one unit, with a hover tooltip,
// a legend when there are two series, and a table view. The sparkline is a single series with the
// budget year marked. Colors: --series-1 / --series-2 (validated for light and dark surfaces).
import { useState } from "react";
import type { BudgetDetail, BudgetSummary } from "../api/types";
import { fy, money } from "../lib/format";

const yearsOf = (edition: number) => [edition - 2, edition - 1, edition, edition + 1, edition + 2, edition + 3, edition + 4];

export function Sparkline({ b }: { b: BudgetSummary }) {
  const ys = yearsOf(b.edition);
  const vals = ys.map((y) => b.funding[String(y)] ?? null);
  const max = Math.max(...vals.filter((v): v is number => v != null), 0.001);
  const w = 84;
  const h = 22;
  const step = w / (ys.length - 1);
  const pts = vals.map((v, i) => (v == null ? null : ([i * step, h - 2 - (v / max) * (h - 4)] as const)));
  const path = pts
    .filter((p): p is readonly [number, number] => p !== null)
    .map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + "," + p[1].toFixed(1))
    .join("");
  const cur = pts[2];
  return (
    <svg className="spark" width={w} height={h} viewBox={`-2 -2 ${w + 4} ${h + 4}`} aria-hidden="true">
      <path d={path} />
      {cur ? <circle cx={cur[0]} cy={cur[1]} r={2.5} /> : null}
    </svg>
  );
}

export function FundingChart({ b }: { b: BudgetDetail }) {
  const [hover, setHover] = useState<number | null>(null);
  const ys = yearsOf(b.edition);
  const cur = ys.map((y) => b.funding[String(y)] ?? null);
  const old = ys.map((y) => b.previous?.funding[String(y)] ?? null);
  const max = Math.max(...[...cur, ...old].filter((v): v is number => v != null), 0.001);
  const W = 440;
  const H = 162;
  const pad = { l: 44, r: 8, t: 22, b: 22 };  // top room for the budget year's value label over the tallest bar
  const cw = (W - pad.l - pad.r) / ys.length;
  const bw = Math.min(28, cw - 8);
  const y = (v: number) => pad.t + (H - pad.t - pad.b) * (1 - v / max);
  const label = (i: number) => (i === 0 ? "Actual" : i === 1 ? "Current" : i === 2 ? "Request" : "Plan");
  const cx = (i: number) => pad.l + i * cw + cw / 2;
  return (
    <>
      <div className="chart-wrap">
        <svg className="fchart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Funding by fiscal year">
          {[0, max / 2, max].map((t) => (
            <g key={t}>
              <line className="grid" x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} />
              <text className="ax" x={pad.l - 6} y={y(t) + 4} textAnchor="end">
                {money(t)}
              </text>
            </g>
          ))}
          {cur.map((v, i) => {
            if (v == null) return null;
            const x = pad.l + i * cw + (cw - bw) / 2;
            const top = y(v);
            const h = Math.max(0, H - pad.b - top);
            const r = Math.min(4, h);
            return (
              <path
                key={i}
                className={`bar ${i === 2 ? "req" : ""}`}
                d={`M${x},${H - pad.b}V${top + r}Q${x},${top} ${x + r},${top}H${x + bw - r}Q${x + bw},${top} ${x + bw},${top + r}V${H - pad.b}Z`}
              />
            );
          })}
          {old.map((v, i) => (v == null ? null : <circle key={i} className="plan" cx={cx(i)} cy={y(v)} r={4.5} />))}
          {ys.map((yr, i) => (
            <text key={yr} className={`ax ${i === 2 ? "strong" : ""}`} x={cx(i)} y={H - 6} textAnchor="middle">
              {fy(yr)}
            </text>
          ))}
          {cur[2] != null ? (
            <text className="val" x={cx(2)} y={y(cur[2]) - 6} textAnchor="middle">
              {money(cur[2])}
            </text>
          ) : null}
          {ys.map((yr, i) => (
            <rect
              key={yr}
              className={`hit ${hover === i ? "on" : ""}`}
              data-i={i}
              x={pad.l + i * cw}
              y={pad.t}
              width={cw}
              height={H - pad.t - pad.b}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          ))}
        </svg>
        {hover !== null ? (
          <div className="tip" style={{ left: `${Math.min(100 - 32, Math.max(0, (cx(hover) / W) * 100 - 16))}%` }}>
            <b>
              {fy(ys[hover]!)} · {label(hover)}
            </b>
            <div>
              <i className="k cur" />
              {money(cur[hover])}
            </div>
            {b.previous ? (
              <div>
                <i className="k plan" />
                {old[hover] == null ? "Not planned" : money(old[hover]) + " planned"}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="legend">
        <span>
          <i className="k cur" />
          FY{b.edition} budget
        </span>
        {b.previous ? (
          <span>
            <i className="k plan" />
            FY{b.previous.edition} budget’s plan
          </span>
        ) : null}
      </div>
      <details className="tbl">
        <summary>Table</summary>
        <table>
          <thead>
            <tr>
              <th>Year</th>
              <th>FY{b.edition} budget</th>
              {b.previous ? <th>FY{b.previous.edition} plan</th> : null}
            </tr>
          </thead>
          <tbody>
            {ys.map((yr, i) => (
              <tr key={yr}>
                <td>
                  {fy(yr)} {label(i).toLowerCase()}
                </td>
                <td>{money(cur[i])}</td>
                {b.previous ? <td>{money(old[i])}</td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </>
  );
}
